/**
 * CAMADA DA TELA — plataforma (VORTEK) × empresa.
 *
 * Rotas que pertencem à camada VORTEK / Operador (chrome próprio: marca VORTEK,
 * menu da plataforma, switcher mostrando "VORTEK"). Centralizado aqui pra a
 * sidebar (Layout), o switcher (EmpresaSwitcher) e o ESCOPO das telas NUNCA
 * divergirem.
 *
 * ⚠️ `/usuarios` SAIU daqui em 27/07/2026. Ela era a MESMA rota nos dois menus
 * (Pessoas → Usuários, da empresa; e Plataforma → Usuários, do operador), e a
 * tela resolvia o escopo por "é dono da plataforma?" em vez de "qual empresa
 * está ativa nesta aba?". Resultado, pego pelo dono operando o Átomo: clicar em
 * Pessoas → Usuários dentro do Átomo caía no painel da PLATAFORMA, listando
 * usuário de TODAS as empresas — com três informações discordando na MESMA tela
 * (faixa "esta aba continua em Átomo", cabeçalho "VORTEK", lista "todas as
 * empresas"). Escrever ali com a empresa ambígua é o mesmo estrago do
 * cross-tenant fechado em #1418/#1423.
 *
 * Agora são DUAS rotas com DOIS significados:
 *   /usuarios            → usuários DA EMPRESA ATIVA (menu Pessoas)
 *   /operador/usuarios   → usuários DA PLATAFORMA   (menu Plataforma)
 *
 * A regra que trava: NENHUM item do menu da empresa (lib/nav.ts) pode apontar
 * pra uma rota desta lista. Guardrail em tests/usuarios-camada-empresa-vs-plataforma.
 */
export const isOperadorPath = (pathname: string): boolean =>
  pathname.startsWith("/operador") ||
  pathname.startsWith("/manual-de-bordo");

/** Rota da lista de usuários DA PLATAFORMA (só pelo menu do operador). */
export const ROTA_USUARIOS_PLATAFORMA = "/operador/usuarios";
/** Rota da lista de usuários DA EMPRESA ATIVA (menu Pessoas). */
export const ROTA_USUARIOS_EMPRESA = "/usuarios";

export type CamadaDaTela = "plataforma" | "empresa";

/**
 * A ÚNICA fonte que diz em que camada a tela está. Cabeçalho, faixa de empresa
 * e escopo da lista saem TODOS daqui — se um dia discordarem de novo, é porque
 * alguém deixou de perguntar pra esta função.
 */
export const camadaDaRota = (pathname: string): CamadaDaTela =>
  isOperadorPath(pathname) ? "plataforma" : "empresa";

/**
 * AS ÁREAS EM QUE A SUB-ROTA É UMA TELA DIFERENTE, E NÃO UM REGISTRO.
 *
 * `/obras/123` e `/obras/456` são a MESMA tela vendo obras diferentes — colapsar
 * pra `/obras` é o certo: senão a telemetria vira uma linha por registro aberto
 * e ninguém consegue perguntar "quem usa Obras?".
 *
 * `/operador/canais` e `/operador/telemetria` são TELAS DIFERENTES, cada uma com
 * seu código, seu dono e sua decisão de existir. Colapsar as duas em `/operador`
 * é perder justamente a pergunta que o control plane precisa responder.
 */
const AREAS_COM_SUBTELA = ["operador"];

/** Teto de `rota` no servidor: `z.string().min(1).max(120)` em routes/telemetria.ts. */
const LIMITE_ROTA = 120;

/** Segmento que identifica um REGISTRO (id numérico ou uuid), não uma tela. */
const pareceId = (s: string): boolean => /^\d+$/.test(s) || /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(s);

/**
 * A rota como a TELEMETRIA a enxerga — o nome da TELA que foi aberta.
 *
 * 🟥 O QUE ISTO CONSERTA (medido em 20/08/2026, levantamento do Operador):
 * a regra antiga era `"/" + pathname.split("/")[1]`, que colapsava TUDO no
 * primeiro segmento. As 8 sub-telas do Operador viravam a mesma linha
 * `/operador`, e a tabela `uso_paginas` respondia 455 aberturas pra área
 * inteira e ZERO pra cada tela — zeros que eram ARTEFATO DA MEDIÇÃO, não
 * ausência de uso. Ou seja: a pergunta "qual destas telas alguém abre?" era
 * IMEDÍVEL, bem na hora de decidir quais redesenhar e quais enterrar.
 *
 * ⚠️ A SÉRIE QUEBRA AQUI. Toda linha gravada ANTES de 20/08/2026 diz `/operador`
 * mesmo quando a tela aberta era uma sub-tela. Quem for comparar contagem de
 * antes com a de depois está comparando duas réguas: a de antes mede a ÁREA, a
 * de depois mede a TELA. E sub-tela recém-nascida com contagem baixa pode ser
 * janela curta de medição, não abandono — espere a janela encher antes de
 * enterrar qualquer uma.
 */
export function rotaDeTelemetria(pathname: string): string {
  const partes = pathname.split("/");
  const area = partes[1] || "";
  const sub = partes[2] || "";
  if (!area) return "/";
  const soAArea = ("/" + area).slice(0, LIMITE_ROTA);
  if (!AREAS_COM_SUBTELA.includes(area) || !sub || pareceId(sub)) return soAArea;
  const comSubtela = `/${area}/${sub}`;
  // Estourar o limite do servidor faria o insert ser REJEITADO e o pageview
  // sumir calado — a tela perderia a abertura sem ninguém saber. Melhor gravar
  // menos preciso (só a área) do que não gravar: o zero silencioso é o defeito
  // que este arquivo existe pra impedir.
  return comSubtela.length <= LIMITE_ROTA ? comSubtela : soAArea;
}

export type EscopoUsuarios = "todas" | "empresa" | "nenhum";

/**
 * O escopo da tela de usuários. Decidido pela ROTA primeiro (que camada é esta?)
 * e só então pela permissão (você pode ver isto?) — nunca o contrário.
 *
 * - camada plataforma → "todas" pro dono da plataforma; "nenhum" pro resto
 *   (soOperador no App.tsx só barra `cliente`, então admin comum chega aqui);
 * - camada empresa    → "empresa" pro dono da empresa E pro dono da plataforma
 *   (é o mesmo par que o backend aceita em /equipe/*: `isOwner` OU `dono_user_id`);
 *   "nenhum" pro resto.
 *
 * Ver todas as empresas estando DENTRO de uma empresa deixou de ser o padrão
 * silencioso: virou a escolha explícita "ver todas as empresas", que navega pra
 * ROTA_USUARIOS_PLATAFORMA — e aí o cabeçalho vira VORTEK junto.
 */
export function escopoDeUsuarios(a: {
  pathname: string;
  /** users.is_owner — dono da PLATAFORMA. */
  ehOperador: boolean;
  /** empresas.dono_user_id === user.id, na empresa ATIVA da aba. */
  ehDonoDaEmpresa: boolean;
}): EscopoUsuarios {
  if (camadaDaRota(a.pathname) === "plataforma") return a.ehOperador ? "todas" : "nenhum";
  return a.ehDonoDaEmpresa || a.ehOperador ? "empresa" : "nenhum";
}
